import React from 'react';
import { HelpCircle } from 'lucide-react';
import { ANALYSIS_RATINGS } from '../../types/analysis';

interface RatingSlidersProps {
  ratings: {
    crumbStructure: number;
    ovenSpring: number;
    crust: number;
    crumbTexture: number;
  };
  onChange: (ratings: RatingSlidersProps['ratings']) => void;
}

export function RatingSliders({ ratings, onChange }: RatingSlidersProps) {
  const handleChange = (name: string, value: number) => {
    onChange({
      ...ratings,
      [name]: value
    });
  };

  const getValueDescription = (name: string, value: number): string => {
    switch (name) {
      case 'crumbStructure':
        if (value < 30) return 'Dense, tight crumb with no visible holes';
        if (value < 60) return 'Some holes but still relatively dense';
        if (value < 80) return 'Good mix of holes and structure';
        return 'Perfect open crumb with well-distributed alveoli';
      
      case 'ovenSpring':
        if (value < 30) return 'Flat loaf with minimal rise';
        if (value < 60) return 'Some rise but could be better';
        if (value < 80) return 'Good rise with visible ear formation';
        return 'Excellent rise with pronounced ear and scoring pattern';
      
      case 'crust':
        if (value < 30) return 'Pale or burnt crust';
        if (value < 60) return 'Uneven coloring or too thick/thin';
        if (value < 80) return 'Good color with decent thickness';
        return 'Perfect golden-brown color with ideal thickness';
      
      case 'crumbTexture':
        if (value < 30) return 'Gummy or very dry texture';
        if (value < 60) return 'Slightly gummy or slightly dry';
        if (value < 80) return 'Good moisture balance';
        return 'Perfect moisture content with silky texture';
      
      default:
        return '';
    }
  };

  return (
    <div className="space-y-6">
      <h3 className="font-medium text-white">Rate Your Bread</h3>
      <p className="text-sm text-white/80 mb-6">
        Move the sliders to rate different aspects of your bread. Hover over the info icons for detailed descriptions.
      </p>
      
      <div className="space-y-8">
        {ANALYSIS_RATINGS.map((rating) => (
          <div key={rating.name} className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2">
                <span className="font-medium text-white">{rating.label}</span>
                <div className="group relative">
                  <HelpCircle className="w-4 h-4 text-white/50" />
                  <div className="absolute left-1/2 bottom-full mb-2 -translate-x-1/2 w-48 p-2 bg-gray-900 text-white rounded-lg text-xs opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                    {rating.description}
                  </div>
                </div>
              </label>
              <span className="text-sm font-medium text-white">{ratings[rating.name as keyof typeof ratings]}%</span>
            </div>
            
            <input
              type="range"
              min="0"
              max="100"
              value={ratings[rating.name as keyof typeof ratings]}
              onChange={(e) => handleChange(rating.name, parseInt(e.target.value))}
              className="w-full h-2 bg-white/10 rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-amber-400 [&::-webkit-slider-thumb]:cursor-pointer"
            />
            
            <div className="flex justify-between text-xs text-white/50">
              <span>Poor</span>
              <span>Excellent</span>
            </div>

            <p className="text-sm text-amber-300 mt-1">
              {getValueDescription(rating.name, ratings[rating.name as keyof typeof ratings])}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}